import PinIcon from "@atlaskit/icon/core/pin";
import {token} from "@atlaskit/tokens";
import type {DragEndEvent} from "@dnd-kit/core";
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {twJoin} from "tailwind-merge";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {KanbanCardSkeleton} from "~/components/ottopilotDataViewer/kanbanCardSkeleton";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import type {KanbanBoardColumnConfig, OttopilotView, ViewType} from "~/components/ottopilotDataViewer/view";
import {
    useKanbanColumnRowCount,
    useKanbanColumnRowCounts,
    useKanbanColumnRows,
} from "~/components/ottopilotDataViewer/viewDataHooks";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

const KANBAN_INITIAL_SKELETON_COUNT = 3;

export function KanbanBoardView<
    T extends {
        id: Uuid;
    },
>({
    controller,
    columnConfig,
    filterFiledId: filterFieldId,
    currentView,
}: {
    controller: OttopilotDataViewerController<T>;
    columnConfig: Array<KanbanBoardColumnConfig>;
    currentView: OttopilotView<T> & {viewType: ViewType.kanbanBoard};
    filterFiledId: Uuid | string; // column id or name on which data is to be grouped
}) {
    const kanbanColumnConfig = controller.kanbanColumnConfig;

    // Derive column order from controller config (lifted state) or fall back to default
    const columnOrder: Array<Uuid> = useMemo(() => {
        if (kanbanColumnConfig !== null) {
            const allIds = columnConfig.map((c) => c.id);
            return [
                ...kanbanColumnConfig.orderedColumnIds.filter((id) => allIds.includes(id)),
                ...allIds.filter((id) => kanbanColumnConfig.orderedColumnIds.includes(id) === false),
            ];
        }
        return columnConfig.map((col) => col.id);
    }, [kanbanColumnConfig, columnConfig]);

    // Sync column order back to controller when drag-and-drop happens inside the board
    const setColumnOrder = useCallback(
        (updater: (prev: Array<Uuid>) => Array<Uuid>) => {
            const nextOrder = updater(columnOrder);
            const hiddenIds = kanbanColumnConfig !== null ? kanbanColumnConfig.hiddenColumnIds : [];
            const pinnedId = kanbanColumnConfig !== null ? kanbanColumnConfig.pinnedColumnId : null;
            controller.setKanbanColumnConfig({
                revisionId: generateUuid(),
                orderedColumnIds: nextOrder,
                hiddenColumnIds: hiddenIds,
                pinnedColumnId: pinnedId,
            });
        },
        [columnOrder, kanbanColumnConfig, controller],
    );

    // Derive hidden column IDs from controller config
    const hiddenColumnIds: Set<Uuid> = useMemo(() => {
        if (kanbanColumnConfig !== null) {
            return new Set(kanbanColumnConfig.hiddenColumnIds);
        }
        return new Set<Uuid>();
    }, [kanbanColumnConfig]);

    // Ensure columnOrder stays in sync with columnConfig when columns are added/removed
    const orderedColumnConfig = useMemo(() => {
        const columnMap = new Map(columnConfig.map((col) => [col.id, col]));
        const ordered: Array<KanbanBoardColumnConfig> = [];

        // Add visible columns in order
        for (const id of columnOrder) {
            if (hiddenColumnIds.has(id)) {
                continue;
            }
            const col = columnMap.get(id);
            if (col !== undefined) {
                ordered.push(col);
                columnMap.delete(id);
            }
        }

        // Add any new columns not in order (also skip hidden)
        for (const [id, col] of columnMap) {
            if (hiddenColumnIds.has(id) === false) {
                ordered.push(col);
            }
        }

        return ordered;
    }, [columnConfig, columnOrder, hiddenColumnIds]);

    // Pinned column: lifted to controller
    const pinnedColumn: Uuid | null = kanbanColumnConfig !== null ? kanbanColumnConfig.pinnedColumnId : null;
    const hideColumnPin = currentView.kanbanLayout?.hideColumnPin === true;
    const effectivePinnedColumn = hideColumnPin === true ? null : pinnedColumn;

    const setPinnedColumn = useCallback(
        (id: Uuid | null) => {
            const currentOrder = kanbanColumnConfig !== null ? kanbanColumnConfig.orderedColumnIds : columnOrder;
            const hiddenIds = kanbanColumnConfig !== null ? kanbanColumnConfig.hiddenColumnIds : [];
            controller.setKanbanColumnConfig({
                revisionId: generateUuid(),
                orderedColumnIds: currentOrder,
                hiddenColumnIds: hiddenIds,
                pinnedColumnId: id,
            });
        },
        [kanbanColumnConfig, columnOrder, controller],
    );

    const [hoveredColumn, setHoveredColumn] = useState<Uuid | null>(null);
    const hasKanbanColumnTotalCounts = controller.kanbanColumnRowCountsQueryFn !== undefined;
    const kanbanColumnRowCountsQuery = useKanbanColumnRowCounts(controller);
    const kanbanColumnTotalCountsById = kanbanColumnRowCountsQuery.data;
    const kanbanColumnCountsPending =
        hasKanbanColumnTotalCounts === true && kanbanColumnRowCountsQuery.isPending === true;
    const kanbanColumnCountsUsePerColumnFallback =
        hasKanbanColumnTotalCounts === true && kanbanColumnRowCountsQuery.isError === true;

    const resolveKanbanColumnTotalCount = useCallback(
        (columnId: Uuid): number | undefined => {
            if (kanbanColumnTotalCountsById === undefined) {
                return undefined;
            }
            return kanbanColumnTotalCountsById[String(columnId)] ?? 0;
        },
        [kanbanColumnTotalCountsById],
    );

    // DnD Kit sensors - PointerSensor for desktop, TouchSensor for mobile
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before drag starts
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250, // 250ms delay to differentiate from scrolling
                tolerance: 5, // 5px tolerance during delay
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Handle drag end - reorder columns
    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const {active, over} = event;

            if (over !== null && over !== undefined && active.id !== over.id) {
                setColumnOrder((items) => {
                    const oldIndex = items.indexOf(active.id as Uuid);
                    const newIndex = items.indexOf(over.id as Uuid);

                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        },
        [setColumnOrder],
    );

    const {leftPinnedColumns, rightPinnedColumns, filteredColumns} = useMemo(() => {
        if (effectivePinnedColumn === null) {
            return {leftPinnedColumns: [], rightPinnedColumns: [], filteredColumns: orderedColumnConfig};
        }
        const leftPinnedColumns: Array<KanbanBoardColumnConfig> = [];
        const rightPinnedColumns: Array<KanbanBoardColumnConfig> = [];
        const filteredColumns: Array<KanbanBoardColumnConfig> = [];
        let found = false;
        for (const col of orderedColumnConfig) {
            if (col.id === effectivePinnedColumn) {
                filteredColumns.push(col);
                found = true;
                continue;
            } else {
                if (!found) {
                    leftPinnedColumns.push(col);
                } else {
                    rightPinnedColumns.push(col);
                }
            }
        }
        return {leftPinnedColumns, rightPinnedColumns, filteredColumns};
    }, [effectivePinnedColumn, orderedColumnConfig]);

    // Build grid template columns dynamically based on pinned state
    const gridTemplateColumns = useMemo(() => {
        if (effectivePinnedColumn === null) {
            return `repeat(${filteredColumns.length}, minmax(280px, 1fr))`;
        }
        const parts: string[] = [];
        if (leftPinnedColumns.length > 0) {
            // Give enough space for rotated column headers (approx 2.5rem per column)
            parts.push(`minmax(0,max-content)`);
        }
        parts.push("minmax(280px, 1fr)"); // Center pinned column
        if (rightPinnedColumns.length > 0) {
            // Give enough space for rotated column headers (approx 2.5rem per column)
            parts.push(`minmax(0,max-content)`);
        }
        return parts.join(" ");
    }, [effectivePinnedColumn, filteredColumns.length, leftPinnedColumns.length, rightPinnedColumns.length]);

    // Handle clicking on a pinned column to make it the new pinned column
    const handlePinnedColumnClick = useCallback(
        (columnId: Uuid) => {
            setPinnedColumn(columnId);
        },
        [setPinnedColumn],
    );

    /** Pinned focus column, or a single status column (e.g. Emotorad sidebar status nav) — cards in a 3-across grid. */
    const isMobileViewport = useCheckMobile();
    const columnMinWidthPx = currentView.kanbanLayout?.columnMinWidthPx ?? 328;
    const columnGapPx = currentView.kanbanLayout?.columnGapPx;
    const mobileCardsGridTemplateColumns = currentView.kanbanLayout?.mobileCardsGridTemplateColumns;

    const cardsGridTemplateColumns =
        isMobileViewport === true && mobileCardsGridTemplateColumns !== undefined
            ? mobileCardsGridTemplateColumns
            : effectivePinnedColumn !== null
              ? `repeat(auto-fill, minmax(${Math.min(columnMinWidthPx, 280)}px, 1fr))`
              : filteredColumns.length === 1
                ? "repeat(3, minmax(0, 1fr))"
                : "1fr";

    return (
        <div className="relative h-full min-h-0 overflow-x-auto overflow-y-hidden scroll-smooth">
            <div className="h-full min-h-0">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={
                            pinnedColumn === null ? filteredColumns.map((c) => c.id) : filteredColumns.map((c) => c.id)
                        }
                        strategy={horizontalListSortingStrategy}
                    >
                        <div
                            className={twJoin(
                                "grid w-full h-full min-h-0",
                                columnGapPx === undefined && "gap-ds-space-250",
                            )}
                            style={{
                                gap: columnGapPx !== undefined ? `${columnGapPx}px` : undefined,
                                gridTemplateColumns:
                                    effectivePinnedColumn === null
                                        ? `repeat(${filteredColumns.length}, minmax(${columnMinWidthPx}px, 1fr))`
                                        : gridTemplateColumns,
                                gridTemplateRows: "minmax(0, 1fr)",
                            }}
                        >
                            {effectivePinnedColumn !== null && leftPinnedColumns.length > 0 && (
                                <div className="grid gap-ds-space-200 h-max sticky top-0 z-10">
                                    <ItemBuilder
                                        items={leftPinnedColumns}
                                        itemBuilder={(column) => (
                                            // <PinnedColumnButton
                                            //     key={column.id}
                                            //     column={column}
                                            //     onClick={() => handlePinnedColumnClick(column.id)}
                                            // />
                                            <SortableColumn
                                                key={column.id}
                                                controller={controller}
                                                column={column}
                                                pinnedColumn={effectivePinnedColumn}
                                                hoveredColumn={hoveredColumn}
                                                setHoveredColumn={setHoveredColumn}
                                                setPinnedColumn={setPinnedColumn}
                                                filterFieldId={filterFieldId}
                                                currentView={currentView}
                                                onClickPinColumn={handlePinnedColumnClick}
                                                disabled={effectivePinnedColumn !== null}
                                                initialSkeletonCount={KANBAN_INITIAL_SKELETON_COUNT}
                                                cardsGridTemplateColumns={cardsGridTemplateColumns}
                                                columnTotalCount={resolveKanbanColumnTotalCount(column.id)}
                                                showColumnCountPending={kanbanColumnCountsPending}
                                                usePerColumnCountFallback={kanbanColumnCountsUsePerColumnFallback}
                                                hideColumnPin={hideColumnPin}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                            {filteredColumns.map((column) => (
                                <SortableColumn
                                    key={column.id}
                                    controller={controller}
                                    column={column}
                                    pinnedColumn={effectivePinnedColumn}
                                    hoveredColumn={hoveredColumn}
                                    setHoveredColumn={setHoveredColumn}
                                    setPinnedColumn={setPinnedColumn}
                                    filterFieldId={filterFieldId}
                                    currentView={currentView}
                                    onClickPinColumn={handlePinnedColumnClick}
                                    disabled={effectivePinnedColumn !== null}
                                    initialSkeletonCount={KANBAN_INITIAL_SKELETON_COUNT}
                                    cardsGridTemplateColumns={cardsGridTemplateColumns}
                                    columnTotalCount={resolveKanbanColumnTotalCount(column.id)}
                                    showColumnCountPending={kanbanColumnCountsPending}
                                    usePerColumnCountFallback={kanbanColumnCountsUsePerColumnFallback}
                                    hideColumnPin={hideColumnPin}
                                />
                            ))}
                            {effectivePinnedColumn !== null && rightPinnedColumns.length > 0 && (
                                <div className="grid gap-ds-space-200 h-max sticky top-0 z-10">
                                    <ItemBuilder
                                        items={rightPinnedColumns}
                                        itemBuilder={(column) => (
                                            // <PinnedColumnButton
                                            //     key={column.id}
                                            //     column={column}
                                            //     onClick={() => handlePinnedColumnClick(column.id)}
                                            // />
                                            <SortableColumn
                                                key={column.id}
                                                controller={controller}
                                                column={column}
                                                pinnedColumn={effectivePinnedColumn}
                                                hoveredColumn={hoveredColumn}
                                                setHoveredColumn={setHoveredColumn}
                                                setPinnedColumn={setPinnedColumn}
                                                filterFieldId={filterFieldId}
                                                currentView={currentView}
                                                onClickPinColumn={handlePinnedColumnClick}
                                                disabled={effectivePinnedColumn !== null}
                                                initialSkeletonCount={KANBAN_INITIAL_SKELETON_COUNT}
                                                cardsGridTemplateColumns={cardsGridTemplateColumns}
                                                columnTotalCount={resolveKanbanColumnTotalCount(column.id)}
                                                showColumnCountPending={kanbanColumnCountsPending}
                                                usePerColumnCountFallback={kanbanColumnCountsUsePerColumnFallback}
                                                hideColumnPin={hideColumnPin}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>
        </div>
    );
}

type SortableColumnProps<T extends {id: Uuid}> = {
    controller: OttopilotDataViewerController<T>;
    column: KanbanBoardColumnConfig;
    pinnedColumn: Uuid | null;
    hoveredColumn: Uuid | null;
    setHoveredColumn: (id: Uuid | null) => void;
    setPinnedColumn: (id: Uuid | null) => void;
    filterFieldId: Uuid | string;
    currentView: OttopilotView<T> & {viewType: ViewType.kanbanBoard};
    disabled?: boolean;
    onClickPinColumn: (id: Uuid) => void;
    initialSkeletonCount: number;
    cardsGridTemplateColumns: string;
    columnTotalCount?: number;
    showColumnCountPending?: boolean;
    usePerColumnCountFallback?: boolean;
    hideColumnPin?: boolean;
};

// Sortable column wrapper component for drag-and-drop
function SortableColumn<T extends {id: Uuid}>(props: SortableColumnProps<T>) {
    const hasGlobalKanbanColumnTotalCounts = props.controller.kanbanColumnRowCountsQueryFn !== undefined;
    const usePerColumnCount =
        hasGlobalKanbanColumnTotalCounts === false || props.usePerColumnCountFallback === true;
    const perColumnCountQuery = useKanbanColumnRowCount({
        controller: props.controller,
        column: props.column,
        filterFieldId: props.filterFieldId,
        enabled: usePerColumnCount,
    });
    const resolvedColumnTotalCount =
        usePerColumnCount === true
            ? perColumnCountQuery.data
            : props.columnTotalCount;
    const resolvedShowColumnCountPending =
        usePerColumnCount === true
            ? perColumnCountQuery.isPending === true
            : props.showColumnCountPending;

    if (props.pinnedColumn !== null && props.pinnedColumn !== props.column.id) {
        return (
            <PinnedColumnButton
                column={props.column}
                onClick={() => props.onClickPinColumn(props.column.id)}
                itemsCount={resolvedColumnTotalCount}
                showCountPending={resolvedShowColumnCountPending}
            />
        );
    }
    return (
        <SortableColumnDataColumn
            {...props}
            columnTotalCount={resolvedColumnTotalCount}
            showColumnCountPending={resolvedShowColumnCountPending}
        />
    );
}

function SortableColumnDataColumn<T extends {id: Uuid}>({
    controller,
    column,
    pinnedColumn,
    hoveredColumn,
    setHoveredColumn,
    setPinnedColumn,
    filterFieldId,
    currentView,
    disabled,
    onClickPinColumn,
    initialSkeletonCount,
    cardsGridTemplateColumns,
    columnTotalCount,
    showColumnCountPending,
    hideColumnPin,
}: SortableColumnProps<T>) {
    const rowsQuery = useKanbanColumnRows({
        controller: controller,
        column: column,
        filterFieldId: filterFieldId,
    });
    const showInitialColumnSkeleton = rowsQuery.status === "pending";
    const showColumnRefetchSkeleton =
        rowsQuery.status === "success" && (rowsQuery.isFetching === true || rowsQuery.isFetchingNextPage === true);
    const items = useMemo(() => {
        if (rowsQuery.status !== "success") {
            return [];
        }
        return rowsQuery.data.pages.flatMap((page) => page);
    }, [rowsQuery.status, rowsQuery.data]);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (rowsQuery.hasNextPage !== true) {
            return;
        }
        if (rowsQuery.isFetchingNextPage === true) {
            return;
        }
        const sentinel = sentinelRef.current;
        const root = scrollContainerRef.current;
        if (sentinel === null || root === null) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry === undefined || entry.isIntersecting !== true) {
                    return;
                }
                if (root.scrollHeight <= root.clientHeight + 8) {
                    return;
                }
                if (rowsQuery.hasNextPage === true && rowsQuery.isFetchingNextPage === false) {
                    void rowsQuery.fetchNextPage();
                }
            },
            {
                root: root,
                threshold: 0.1,
            },
        );
        observer.observe(sentinel);
        return () => {
            observer.disconnect();
        };
    }, [rowsQuery.hasNextPage, rowsQuery.isFetchingNextPage, rowsQuery.fetchNextPage]);

    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: column.id,
        disabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 100 : undefined,
    };
    const hasKanbanColumnTotalCounts = controller.kanbanColumnRowCountsQueryFn !== undefined;
    const headerCount =
        columnTotalCount ?? (hasKanbanColumnTotalCounts === true ? undefined : items.length);
    const showHeaderCountPending =
        showColumnCountPending === true ||
        (hasKanbanColumnTotalCounts === true && columnTotalCount === undefined) ||
        (showInitialColumnSkeleton === true && columnTotalCount === undefined);

    if (pinnedColumn !== null && pinnedColumn !== column.id) {
        return (
            <PinnedColumnButton
                column={column}
                itemsCount={headerCount}
                showCountPending={showHeaderCountPending}
                onClick={() => onClickPinColumn(column.id)}
            />
        );
    }
    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex flex-col h-full min-h-0"
        >
            <div
                className="w-full shrink-0 sticky top-0 z-10 grid grid-flow-col justify-between items-center px-ds-space-150 py-ds-space-100"
                onMouseEnter={() => setHoveredColumn(column.id)}
                onMouseLeave={() => setHoveredColumn(null)}
                style={{
                    background: column.background,
                    borderRadius: column.headerBorderRadius ?? `${token("radius.large")} ${token("radius.large")} 0 0`,
                    borderStyle: "solid",
                    borderWidth: column.headerBorderWidth ?? "0",
                    borderColor: column.headerBorderColor ?? "transparent",
                    cursor: disabled === true ? undefined : "grab",
                }}
                {...(disabled === true ? {} : {...attributes, ...listeners})}
            >
                <div
                    className="w-full text-[0.875rem] font-bold leading-ds-space-300"
                    style={{
                        color: column.textColor ?? token("color.text"),
                    }}
                >
                    {column.displayName}
                    {showHeaderCountPending === false && headerCount !== undefined ? (
                        <span
                            className="text-[0.875rem] font-bold leading-ds-space-300"
                            style={{
                                color: column.textColor ?? token("color.text"),
                            }}
                        >
                            {" "}
                            ({headerCount})
                        </span>
                    ) : (
                        <span
                            aria-hidden={true}
                            className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-300"
                            style={{
                                color: column.textColor ?? token("color.icon.inverse"),
                            }}
                        >
                            (…)
                        </span>
                    )}
                </div>
                {hideColumnPin !== true && (hoveredColumn === column.id || pinnedColumn === column.id) && (
                    <div className="max-sm:hidden">
                        <AnankeIconButton
                            type="button"
                            spacing="compact"
                            onClick={(e) => {
                                e.stopPropagation();
                                setPinnedColumn(column.id === pinnedColumn ? null : column.id);
                            }}
                            icon={PinIcon}
                            label=""
                            appearance="subtle"
                        />
                    </div>
                )}
            </div>
            <div
                ref={scrollContainerRef}
                className="w-full flex-1 min-h-0 overflow-y-auto grid p-ds-space-200 gap-ds-space-200 auto-rows-max"
                style={{
                    gridTemplateColumns: cardsGridTemplateColumns,
                    borderRadius: `0 0 ${token("radius.large")} ${token("radius.large")}`,
                    background: token("color.background.input.hovered"),
                }}
            >
                {showInitialColumnSkeleton === true ? (
                    Array.from({length: initialSkeletonCount}, (_, index) => {
                        return <KanbanCardSkeleton key={`${String(column.id)}-placeholder-${String(index)}`} />;
                    })
                ) : (
                    <>
                        {items.length === 0 && showColumnRefetchSkeleton !== true ? (
                            <div
                                className="grid place-items-center px-ds-space-200 py-ds-space-300 text-center"
                                style={{
                                    gridColumn: cardsGridTemplateColumns !== "1fr" ? "1 / -1" : undefined,
                                }}
                            >
                                <AnankeText color="color.text.subtlest">No data available</AnankeText>
                            </div>
                        ) : (
                            <ItemBuilder
                                items={items}
                                itemBuilder={(item) => (
                                    <Fragment key={item.id}>
                                        {currentView.onKanbanCardClick !== undefined ||
                                        controller.readPanelComponent !== undefined ? (
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                aria-current={controller.activePanelRowId === item.id ? "true" : undefined}
                                                className={twJoin(
                                                    "cursor-pointer rounded-[0.75rem] transition-opacity duration-small hover:opacity-90",
                                                )}
                                                onClick={() => {
                                                    if (currentView.onKanbanCardClick !== undefined) {
                                                        currentView.onKanbanCardClick(item);
                                                        return;
                                                    }
                                                    controller.setCurrentlyOpenRowEngagementPanel({
                                                        rowEngagementType: RowEngagementType.read,
                                                        rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                                        rowEngagementData: item,
                                                    });
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        if (currentView.onKanbanCardClick !== undefined) {
                                                            currentView.onKanbanCardClick(item);
                                                            return;
                                                        }
                                                        controller.setCurrentlyOpenRowEngagementPanel({
                                                            rowEngagementType: RowEngagementType.read,
                                                            rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                                            rowEngagementData: item,
                                                        });
                                                    }
                                                }}
                                            >
                                                {currentView.cardRenderer(item, {isPanelActive: controller.activePanelRowId === item.id})}
                                            </div>
                                        ) : (
                                            currentView.cardRenderer(item, {isPanelActive: controller.activePanelRowId === item.id})
                                        )}
                                    </Fragment>
                                )}
                            />
                        )}
                        {showColumnRefetchSkeleton === true ? <KanbanCardSkeleton /> : null}
                        <div
                            ref={sentinelRef}
                            className="h-px w-full"
                            style={{
                                gridColumn: cardsGridTemplateColumns !== "1fr" ? "1 / -1" : undefined,
                            }}
                        />
                    </>
                )}
            </div>
            <div></div>
        </div>
    );
}

function PinnedColumnButton({
    column,
    onClick,
    itemsCount,
    showCountPending,
}: {
    column: KanbanBoardColumnConfig;
    onClick: () => void;
    itemsCount?: number;
    showCountPending?: boolean;
}) {
    return (
        <div
            className="h-max [writing-mode:vertical-rl] rotate-180 text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 px-ds-space-100 py-ds-space-150 cursor-pointer transition-opacity hover:opacity-80"
            style={{
                background: column.background,
                borderRadius: token("radius.large"),
                color: column.textColor ?? token("color.icon.inverse"),
            }}
            onClick={onClick}
            title={`Click to pin "${column.displayName}"`}
        >
            {column.displayName}{" "}
            {showCountPending === true ? "(…)" : itemsCount !== undefined ? `(${itemsCount})` : null}
        </div>
    );
}
